import { useState } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { styles, colors } from '../components/UI';

// Full-page forced password change. Shown when user.must_change_password is
// true (set on every account creation -- manual or bulk import). Cannot be
// dismissed; the only way out is to set a new password.
export default function ForcePasswordChange() {
  const { user, setUser, exitViewAs } = useAuth();
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Safety net: the App's Protected wrapper now skips this screen entirely
  // when admin is impersonating via View As, but in case any future code
  // path lands an impersonating admin here, give them an unmissable exit
  // back to their own session. Without this, an admin can get fully
  // trapped (no nav, no logout button on this screen).
  const exitImpersonation = async () => {
    try { await exitViewAs(); } catch (_) {}
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next !== confirm) return setError('Passwords do not match.');
    if (next.length < 8) return setError('Password must be at least 8 characters.');
    if (next === 'ChangeMe2026!') return setError('Pick a different password than the temp one.');
    setBusy(true);
    try {
      await api.initialPassword(next);
      // Refresh /auth/me so must_change_password flips to false in our context
      const me = await api.me();
      setUser(me);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: colors.charcoal, padding: 16
    }}>
      <form onSubmit={submit} style={{
        background: '#fff', padding: 32, borderRadius: 10, width: 460, maxWidth: '100%',
        boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
      }}>
        <h1 style={{ ...styles.h1, color: colors.orange, marginTop: 0 }}>Set Your Password</h1>
        <p style={{ fontSize: 14, color: colors.text, marginTop: 0 }}>
          Welcome, {user?.full_name || user?.username}. Before you can use SSS-CRM, please set a personal password to replace the temporary one.
        </p>
        {error && <div style={styles.errorBox}>{error}</div>}
        <div style={{ marginBottom: 12 }}>
          <label style={styles.label}>New Password</label>
          <input
            type="password"
            style={styles.input}
            value={next}
            onChange={e => setNext(e.target.value)}
            required minLength={8} autoFocus
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={styles.label}>Confirm New Password</label>
          <input
            type="password"
            style={styles.input}
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            required minLength={8}
          />
        </div>
        <button type="submit" disabled={busy} style={{ ...styles.btn, width: '100%', padding: 10 }}>
          {busy ? 'Saving...' : 'Set Password & Continue'}
        </button>
        <p style={{ fontSize: 11, color: colors.textMuted, marginTop: 16, marginBottom: 0 }}>
          At least 8 characters. Don't reuse the temporary password.
        </p>
        {user?.is_preview && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${colors.border}` }}>
            <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 8px 0' }}>
              You are viewing as <strong>{user.full_name}</strong>. They haven't set their password yet.
              Don't change it for them — exit back to your own session.
            </p>
            <button
              type="button"
              onClick={exitImpersonation}
              style={{ ...styles.btnSecondary, width: '100%', padding: 8 }}
            >
              ← Exit View As
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
